import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { money } from "@/lib/format";
import { useBrand } from "@/components/BrandProvider";
import { Button } from "@/components/ui/button";
import { ShieldCheck, CheckCircle2, XCircle, Clock, Ban, Loader2 } from "lucide-react";

export default function PaymentPage() {
  const { linkId } = useParams();
  const [link, setLink] = useState(null);
  const [error, setError] = useState("");
  const [acting, setActing] = useState("");
  const [pollingMsg, setPollingMsg] = useState("");
  const marca = useBrand() || {};
  const logoPago = marca.logo_url || marca.logo_icono_url; // Fix K

  const load = async () => {
    try {
      const r = await api.get(`/pagos/publico/${linkId}`);
      setLink(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "No se encontró este link de pago.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [linkId]);

  useEffect(() => {
    if (!link || link.estado !== "pendiente") return;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [link?.estado]);

  const act = async (accion) => {
    setActing(accion);
    try {
      const r = await api.post(`/pagos/publico/${linkId}/${accion}`);
      if (accion === "aprobar-polling") setPollingMsg(r.data.mensaje);
      else load();
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo procesar la acción.");
    } finally {
      setActing("");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6" data-testid="payment-page-error">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm text-center">
          <XCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <p className="text-slate-700">{error}</p>
        </div>
      </div>
    );
  }
  if (!link) {
    return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-400">Cargando…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6" data-testid="payment-page">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-sm w-full">
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-400" />
          <span className="font-semibold text-sm tracking-wide">Pasarela de Pago</span>
          {link.es_simulado && <span className="ml-auto text-[10px] font-bold bg-amber-400 text-slate-900 rounded-full px-2 py-0.5">DEMO</span>}
        </div>
        <div className="p-6 space-y-5">
          {logoPago && (
            <img src={`${BACKEND_URL}${logoPago}`} alt={marca.nombre_comercial || "Logo"}
              className="mx-auto w-[140px] max-h-24 object-contain" data-testid="payment-page-logo" />
          )}
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide">{link.nombre_negocio}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="payment-page-monto">{money(link.monto)}</p>
            <p className="text-sm text-slate-500 mt-1">{link.descripcion}</p>
            <p className="text-xs text-slate-400 mt-1">Pedido #{link.numero_pedido}</p>
          </div>

          {link.estado === "pendiente" && link.acepta_pagos !== false && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-center gap-2 text-slate-600 text-sm">
              <Clock className="h-4 w-4 shrink-0" /> Esperando confirmación de pago…
            </div>
          )}
          {/* Fix Q (2026-10-02): link de la pasarela de prueba fuera del simulador: no acepta pagos */}
          {link.estado === "pendiente" && link.acepta_pagos === false && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-center gap-2 text-amber-700 text-sm" data-testid="payment-page-no-acepta">
              <Ban className="h-4 w-4 shrink-0" /> Este link es de prueba y no acepta pagos. Escríbenos por el chat para coordinar el pago.
            </div>
          )}
          {link.estado === "pagado" && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-emerald-700 text-sm" data-testid="payment-page-pagado">
              <CheckCircle2 className="h-4 w-4 shrink-0" /> Pago aprobado. ¡Gracias!
            </div>
          )}
          {link.estado === "rechazado" && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 flex items-center gap-2 text-red-700 text-sm" data-testid="payment-page-rechazado">
              <XCircle className="h-4 w-4 shrink-0" /> El pago fue rechazado.
            </div>
          )}
          {link.estado === "vencido" && (
            <div className="rounded-xl bg-slate-100 border border-slate-200 p-3 flex items-center gap-2 text-slate-500 text-sm">
              <Ban className="h-4 w-4 shrink-0" /> Este link de pago venció.
            </div>
          )}
          {link.estado === "anulado" && (
            <div className="rounded-xl bg-slate-100 border border-slate-200 p-3 flex items-center gap-2 text-slate-500 text-sm">
              <Ban className="h-4 w-4 shrink-0" /> Este link de pago fue anulado.
            </div>
          )}

          {link.estado === "pendiente" && link.es_simulado && link.acepta_pagos !== false && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <p className="text-[11px] text-slate-400">Modo demostración — simulá la respuesta de la pasarela:</p>
              <Button disabled={!!acting} onClick={() => act("aprobar-webhook")} className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-600" data-testid="payment-approve-webhook">
                {acting === "aprobar-webhook" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pagar aprobado (webhook instantáneo)"}
              </Button>
              <Button disabled={!!acting} onClick={() => act("aprobar-polling")} variant="outline" className="w-full rounded-xl" data-testid="payment-approve-polling">
                {acting === "aprobar-polling" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pagar aprobado (sin webhook, por consulta)"}
              </Button>
              {pollingMsg && <p className="text-xs text-amber-600" data-testid="payment-polling-msg">{pollingMsg}</p>}
              <Button disabled={!!acting} onClick={() => act("rechazar")} variant="ghost" className="w-full rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50" data-testid="payment-reject">
                Rechazar pago
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
